import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Layout from '../../components/layout/Layout'
import { useAuthStore } from '../../store/authStore'
import back_app from '../../lib/back_app'

import xdate from '../../../../../util/date.js'

type Campaign = {
  id: string
  name: string
  description: string | null
  status: 'active' | 'paused' | 'completed' | 'draft' | 'disable'
  budget_info: any
  sched: any
  ts?: {insert: Date, modified: Date}
}

export default function Campaigns() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadCampaigns()
  }, [user])

  const loadCampaigns = async () => {
    if (!user) return
    setLoading(true)
    let res = await back_app.campaign_list()
    if (!res.err)
      setCampaigns(res.campaigns || res || [])
    setLoading(false)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700'
      case 'paused':
      case 'pause':
        return 'bg-yellow-100 text-yellow-700'
      case 'completed':
      case 'disable':
        return 'bg-gray-100 text-gray-700'
      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-dark mb-2">Campaigns</h1>
            <p className="text-gray-600">Create and manage your brand campaigns</p>
          </div>
          <button
            onClick={() => navigate('/brand/campaigns/new')}
            className="bg-gradient-to-r from-primary-purple to-primary-blue text-white px-6 py-3 rounded-xl font-bold hover:shadow-lg transition"
          >
            + New Campaign
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-12 h-12 border-4 border-primary-purple border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : campaigns.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-12 h-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-dark mb-2">No campaigns yet</h3>
            <p className="text-gray-600 mb-4">Create your first campaign to start reaching creators</p>
            <button
              onClick={() => navigate('/brand/campaigns/new')}
              className="bg-gradient-to-r from-primary-purple to-primary-blue text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition"
            >
              Create Campaign
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {campaigns.map((campaign) => (
              <div
                key={campaign.id}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition cursor-pointer"
                onClick={() => navigate(`/brand/campaigns/${campaign.id}`)}
              >
                <div className="bg-gradient-to-r from-primary-purple to-primary-blue p-6 text-white">
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-xl font-bold">{campaign.name}</h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(campaign.status)}`}>
                      {campaign.status}
                    </span>
                  </div>
                  <p className="text-white/80 text-sm line-clamp-2">
                    {campaign.description || 'No description'}
                  </p>
                </div>

                <div className="p-6 space-y-4">
                  {campaign.budget_info?.budget && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Budget</span>
                      <span className="text-lg font-bold text-dark">${Number(campaign.budget_info.budget).toLocaleString()}</span>
                    </div>
                  )}

                  {campaign.budget_info?.cost_per_view && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Cost/View</span>
                      <span className="text-sm font-semibold text-dark">${campaign.budget_info.cost_per_view}</span>
                    </div>
                  )}

                  <div className="pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{campaign.ts?.insert ? xdate.fmt_lbl_short(campaign.ts.insert) : ''}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  )
}